import type {
    ResponseStatus,
    WebAppleLoginApiRequest,
    WebFacebookLoginApiRequest,
    WebGoogleLoginApiRequest,
    WebKlarnaLoginApiRequest,
    WebMemberTokenApiRequest,
    WebMemberTokenApiResponse
} from 'tradera/state/services/types/webapi-auth-generated';
import {
    type FetchBaseQueryError,
    createApi
} from '@reduxjs/toolkit/query/react';
import type { SerializedError } from '@reduxjs/toolkit';
import { HTTP_STATUS_CODES } from 'tradera/constants/http-status-codes';
import {
    type WebApiError,
    isomorphicWebApiBaseQuery
} from 'tradera/state/services/utils/webapi-base-query';

type LoginResponse =
    | {
          data: WebMemberTokenApiResponse;
      }
    | {
          error: FetchBaseQueryError | SerializedError | WebApiError;
      };

const hasData = (obj: unknown): obj is { data: { responseStatus?: unknown } } =>
    obj !== null &&
    typeof obj === 'object' &&
    'data' in obj &&
    obj.data !== null;

const isResponseStatus = (errorData: unknown): errorData is ResponseStatus =>
    errorData !== null &&
    typeof errorData === 'object' &&
    'errorCode' in errorData &&
    errorData.errorCode !== null &&
    typeof errorData.errorCode === 'string';

const hasResponseStatus = (
    error: unknown
): error is {
    data: { responseStatus: ResponseStatus };
} => hasData(error) && isResponseStatus(error.data.responseStatus);

const hasFetchBaseQueryError = (
    response: unknown
): response is { error: FetchBaseQueryError } =>
    response !== null &&
    typeof response === 'object' &&
    'error' in response &&
    hasData(response.error);

export const getMemberId = (response: LoginResponse) => {
    if ('data' in response) {
        return response.data.memberId;
    }
    return 0;
};

export const isSuccessfulLogin = (response: LoginResponse) =>
    getMemberId(response) !== 0;

const isWebMemberTokenApiResponse = (
    data: unknown
): data is WebMemberTokenApiResponse =>
    data !== null &&
    typeof data === 'object' &&
    'mfaToken' in data &&
    data.mfaToken !== null;

const isUnauthorized = (response: LoginResponse) => {
    if (!hasFetchBaseQueryError(response)) {
        return false;
    }
    const error: FetchBaseQueryError = response.error;
    return error.status === HTTP_STATUS_CODES.UNAUTHORIZED;
};

const hasResponseStatusErrorCode = (
    response: LoginResponse,
    errorCode: string
) =>
    hasFetchBaseQueryError(response) &&
    hasResponseStatus(response.error) &&
    response.error.data.responseStatus.errorCode === errorCode;

export const getResponseStatusErrorCode = (response: LoginResponse) => {
    if (!hasFetchBaseQueryError(response)) {
        return undefined;
    }
    if (hasResponseStatus(response.error)) {
        return response.error.data.responseStatus.errorCode;
    }
    return undefined;
};

export const isIncorrectUsernameOrPassword = (response: LoginResponse) =>
    isUnauthorized(response) &&
    hasResponseStatusErrorCode(response, 'MemberAuth_IncorrectUserOrPassword');

export const isAccountDeleted = (response: LoginResponse) =>
    isUnauthorized(response) &&
    hasResponseStatusErrorCode(response, 'MemberAuth_AccountDeleted');

export const isAccountDeletedWithUnpaidInvoices = (response: LoginResponse) =>
    isUnauthorized(response) &&
    hasResponseStatusErrorCode(
        response,
        'MemberAuth_AccountDeleted_WithUnpaidInvoices'
    );

export const isAccountBlocked = (response: LoginResponse) =>
    isUnauthorized(response) &&
    hasResponseStatusErrorCode(response, 'MemberAuth_StateBlockLogin');

export const isRecaptchaV3ScoreTooLowResponse = (response: LoginResponse) =>
    isUnauthorized(response) &&
    hasResponseStatusErrorCode(response, 'RecaptchaV3ScoreTooLow');

export const isRecaptchaV3TransientErrors = (response: LoginResponse) =>
    isUnauthorized(response) &&
    hasResponseStatusErrorCode(response, 'RecaptchaV3TransientErrors');

export const isRecaptchaV3TokenMissing = (response: LoginResponse) =>
    isUnauthorized(response) &&
    hasResponseStatusErrorCode(response, 'RecaptchaV3TokenMissing');

export const isMultiFactorRequired = (response: LoginResponse) => {
    if (!hasFetchBaseQueryError(response)) {
        return false;
    }
    const error: FetchBaseQueryError = response.error;
    if (error.status !== HTTP_STATUS_CODES.UNAUTHORIZED) {
        return false;
    }
    return isWebMemberTokenApiResponse(error.data);
};

export const getMfaToken = (response: LoginResponse) => {
    if (!hasFetchBaseQueryError(response)) {
        return undefined;
    }
    const error: FetchBaseQueryError = response.error;
    if (isWebMemberTokenApiResponse(error.data)) {
        return error.data.mfaToken || undefined;
    }
    return undefined;
};

export const loginApi = createApi({
    reducerPath: 'loginApi',
    baseQuery: isomorphicWebApiBaseQuery,
    endpoints: (builder) => ({
        login: builder.mutation<
            WebMemberTokenApiResponse,
            WebMemberTokenApiRequest
        >({
            query: (params) => ({
                url: '/api/webapi/auth/web/member/token',
                method: 'POST',
                body: params
            })
        }),
        loginWithGoogle: builder.mutation<
            WebMemberTokenApiResponse,
            WebGoogleLoginApiRequest
        >({
            query: (params) => ({
                url: '/api/webapi/auth/web/member/login/google',
                method: 'POST',
                body: params
            })
        }),
        loginWithKlarna: builder.mutation<
            WebMemberTokenApiResponse,
            WebKlarnaLoginApiRequest
        >({
            query: (params) => ({
                url: '/api/webapi/auth/web/member/login/klarna',
                method: 'POST',
                body: params
            })
        }),
        loginWithFacebook: builder.mutation<
            WebMemberTokenApiResponse,
            WebFacebookLoginApiRequest
        >({
            query: (params) => ({
                url: '/api/webapi/auth/web/member/login/facebook',
                method: 'POST',
                body: params
            })
        }),
        loginWithApple: builder.mutation<
            WebMemberTokenApiResponse,
            WebAppleLoginApiRequest
        >({
            query: (params) => ({
                url: '/api/webapi/auth/web/member/login/apple',
                method: 'POST',
                body: params
            })
        })
    })
});

export const {
    useLoginMutation,
    useLoginWithAppleMutation,
    useLoginWithFacebookMutation,
    useLoginWithGoogleMutation,
    useLoginWithKlarnaMutation
} = loginApi;
